import {
	IRegEdMetadataField,
	IRegEdMetadataForm,
	IRegEdMetadataOption,
	RegEdFieldType,
} from '@Queries/RegEdCompliance/types';
import * as Api from '@ViewModels';
import { CampaignType } from '../../../../models/AdminModels';
import { computeVisibleFields } from '../../../../surveys/containers/SurveyResponses/shared/hooks/useConditionalVisibility';

// `Object.values` on a string enum yields its members in declaration order, so the array index
// matches the integer ordinal an endpoint may send in place of the string member name.
const fieldTypesByOrdinal = Object.values(RegEdFieldType);

export const normalizeFieldType = (raw: unknown): RegEdFieldType | undefined => {
	if (typeof raw === 'string') {
		return (fieldTypesByOrdinal as string[]).includes(raw) ? (raw as RegEdFieldType) : undefined;
	}
	if (typeof raw === 'number') {
		return fieldTypesByOrdinal[raw];
	}
	return undefined;
};

// Options frequently omit `externalId`, and imported `visibleWhen.fieldValue` rules reference the
// option LABEL — so the label is the reliable answer value (`externalId`, when present, equals it).
export const regEdOptionValue = (option: IRegEdMetadataOption): string => option.externalId ?? option.label ?? '';

// Imported catalog strings, not a Levitate enum. No newsletter entry: the catalog offers both a weekly and
// a monthly/quarterly option, and a Levitate newsletter carries no cadence to choose between them.
const CONTENT_TYPE_ANSWER_BY_CAMPAIGN_TYPE: Partial<Record<CampaignType, string>> = {
	[CampaignType.Blog]: 'Website',
	[CampaignType.Email]: 'Letter/Email/Text',
	[CampaignType.Social]: 'Social Media Posts/Message Templates/Advertisement',
};

const CONTENT_TYPE_ANSWERS = Object.values(CONTENT_TYPE_ANSWER_BY_CAMPAIGN_TYPE);

/**
 * Identified by the field offering *every* material type, not by one value: "Website" also appears among the
 * distribution-channel options, so a single value matches several fields. Field ids come from the import and
 * the wording is the broker-dealer's, so neither is ours to key off either.
 *
 * Single-select only — a `MultipleOptions` field answers with an array, and a string would not render.
 */
export const resolveContentTypeAnswer = (
	schema: IRegEdMetadataForm | null | undefined,
	campaignType: CampaignType | undefined
): { fieldId: string; value: string } | undefined => {
	const target = campaignType ? CONTENT_TYPE_ANSWER_BY_CAMPAIGN_TYPE[campaignType] : undefined;
	if (!target) {
		return undefined;
	}
	const candidates = orderedActiveFields(schema).filter(field => {
		if (!field.id || normalizeFieldType(field.fieldType) !== RegEdFieldType.Option) {
			return false;
		}
		const values = new Set((field.options ?? []).map(o => regEdOptionValue(o).trim()));
		return CONTENT_TYPE_ANSWERS.every(answer => values.has(answer));
	});
	if (candidates.length !== 1) {
		return undefined;
	}
	const option = (candidates[0].options ?? []).find(o => regEdOptionValue(o).trim() === target);
	return option ? { fieldId: candidates[0].id!, value: regEdOptionValue(option) } : undefined;
};

/** Every field a visibility rule reads, flattening the nested logical form. */
const visibilityTriggerIds = (visibleWhen: unknown): string[] => {
	const rule = visibleWhen as { _type?: unknown; fieldId?: string; visibilities?: unknown[] } | null;
	if (!rule || typeof rule._type !== 'string') {
		return [];
	}
	if (rule.fieldId) {
		return [rule.fieldId];
	}
	return (rule.visibilities ?? []).flatMap(visibilityTriggerIds);
};

/**
 * Schema order, except a conditional field follows the one that reveals it. AdTrax orders the
 * "if other, please specify…" questions at the end of the form, so answering a checkbox halfway up
 * made its follow-up appear screens below, reading as unrelated to the thing that triggered it.
 *
 * A field whose trigger can't be resolved keeps its schema position rather than being dropped or
 * guessed at, which also makes a circular rule harmless.
 */
export const orderedActiveFields = (schema: IRegEdMetadataForm | null | undefined): IRegEdMetadataField[] => {
	const fields = (schema?.fields ?? [])
		.filter(f => f.isActive !== false && !!f.id)
		.slice()
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

	const byId = new Map(fields.map(f => [f.id!, f]));
	const dependentsOf = new Map<string, IRegEdMetadataField[]>();
	const roots: IRegEdMetadataField[] = [];
	for (const field of fields) {
		// The last trigger in schema order — a rule reading several fields belongs under the latest of
		// them, or it would jump above a question it depends on.
		const trigger = visibilityTriggerIds(field.visibleWhen)
			.filter(id => id !== field.id && byId.has(id))
			.sort((a, b) => fields.findIndex(f => f.id === a) - fields.findIndex(f => f.id === b))
			.pop();
		if (trigger) {
			dependentsOf.set(trigger, [...(dependentsOf.get(trigger) ?? []), field]);
		} else {
			roots.push(field);
		}
	}

	const ordered: IRegEdMetadataField[] = [];
	const placed = new Set<string>();
	const emit = (field: IRegEdMetadataField) => {
		if (placed.has(field.id!)) {
			return;
		}
		placed.add(field.id!);
		ordered.push(field);
		for (const dependent of dependentsOf.get(field.id!) ?? []) {
			emit(dependent);
		}
	};
	roots.forEach(emit);
	fields.forEach(emit);
	return ordered;
};

const hasEvaluableType = (visibleWhen: unknown): visibleWhen is Api.QuestionVisibility =>
	!!visibleWhen && typeof (visibleWhen as { _type?: unknown })._type === 'string';

// Forward `visibleWhen` only when it carries a recognizable `_type` discriminator — anything
// without one is treated as unconditionally visible rather than silently hidden.
export const toSurveyForm = (fields: IRegEdMetadataField[]): Api.SurveyForm => ({
	surveyParts: fields.map(field => {
		const part: Api.SurveyQuestion = {
			id: field.id!,
			_type: 'ShortAnswerQuestion',
			fieldId: field.id!,
			visibleWhen: hasEvaluableType(field.visibleWhen) ? field.visibleWhen : null,
		};
		return part;
	}),
});

// Multi-select answers become comma-separated so the visibility hook's `includes` check works.
export const toFormValues = (answers: Record<string, unknown>): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const [key, value] of Object.entries(answers)) {
		if (Array.isArray(value)) {
			out[key] = value.map(String).join(',');
		} else if (value != null && typeof value !== 'object') {
			out[key] = String(value);
		}
	}
	return out;
};

export const isAnswerEmpty = (value: unknown): boolean =>
	value == null || value === '' || (Array.isArray(value) && value.length === 0);

// A filing must never carry answers the advisor can no longer see or retract; cascades to a
// fixpoint since clearing a controlling field can hide its dependents.
export const stripHiddenFieldAnswers = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): Record<string, unknown> => {
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	if (!surveyForm.surveyParts.length) {
		return answers;
	}
	const governedFieldIds = new Set(fields.map(field => field.id!));
	let current = answers;
	for (let pass = 0; pass <= governedFieldIds.size; pass++) {
		const visible = computeVisibleFields(surveyForm, toFormValues(current));
		let changed = false;
		const next: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(current)) {
			if (governedFieldIds.has(key) && !visible.has(key)) {
				changed = true;
				continue;
			}
			next[key] = value;
		}
		if (!changed) {
			return current;
		}
		current = next;
	}
	return current;
};

// Required-field validation lives client-side — the API only warns on unmapped keys rather than rejecting.
export const findMissingRequiredFieldIds = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>
): string[] => {
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	const visible = surveyForm.surveyParts.length ? computeVisibleFields(surveyForm, toFormValues(answers)) : undefined;
	return fields
		.filter(field => {
			if (!field.isRequired || !field.id) {
				return false;
			}
			if (normalizeFieldType(field.fieldType) === RegEdFieldType.Label) {
				return false;
			}
			if (visible && !visible.has(field.id)) {
				return false;
			}
			return isAnswerEmpty(answers[field.id]);
		})
		.map(field => field.id!);
};

const PLATFORM_ANSWER_BY_PROVIDER: Partial<Record<Api.SocialMediaType, string>> = {
	[Api.SocialMediaType.Facebook]: 'Facebook Business',
	[Api.SocialMediaType.Instagram]: 'Instagram Business',
	[Api.SocialMediaType.LinkedIn]: 'LinkedIn',
};

const PLATFORM_ANSWERS = Object.values(PLATFORM_ANSWER_BY_PROVIDER).filter((v): v is string => !!v);

/**
 * Levitate cannot publish to X, so an advisor working here can never truthfully say a post will run there —
 * the option is offered by the questionnaire, not by us. Matched on the label because that is all the
 * imported schema gives; a bare `X` must match as a whole word or every label containing an x would.
 */
export const isUnpublishableToOption = (label: string | null | undefined): boolean => {
	const normalized = (label ?? '').trim().toLowerCase();
	return normalized.includes('twitter') || normalized.replace(/[^a-z0-9]/g, '') === 'x';
};

/**
 * Identified by visibility, because the platform question repeats verbatim under each material type — the
 * post, the weekly newsletter and the monthly one all offer the same four options, so nothing about the
 * options themselves tells them apart. Only the one revealed by the current answers is the right question.
 */
export const resolvePlatformAnswer = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	providers: readonly Api.SocialMediaType[] | undefined
): { fieldId: string; values: string[] } | undefined => {
	const wanted = [...new Set((providers ?? []).map(p => PLATFORM_ANSWER_BY_PROVIDER[p]).filter(Boolean))];
	if (!wanted.length) {
		return undefined;
	}
	const fields = orderedActiveFields(schema);
	const surveyForm = toSurveyForm(fields);
	const visible = surveyForm.surveyParts.length ? computeVisibleFields(surveyForm, toFormValues(answers)) : undefined;
	const candidates = fields.filter(field => {
		if (!field.id || normalizeFieldType(field.fieldType) !== RegEdFieldType.MultipleOptions) {
			return false;
		}
		if (visible && !visible.has(field.id)) {
			return false;
		}
		const values = new Set((field.options ?? []).map(o => regEdOptionValue(o).trim()));
		return PLATFORM_ANSWERS.every(answer => values.has(answer));
	});
	if (candidates.length !== 1) {
		return undefined;
	}
	const offered = new Set((candidates[0].options ?? []).map(o => regEdOptionValue(o).trim()));
	const values = wanted.filter(value => offered.has(value));
	return values.length ? { fieldId: candidates[0].id!, values } : undefined;
};

/**
 * Fills what the entry point already implies, in one pass so the platform question is resolved against the
 * material answer this same call just supplied. Returns undefined when there is nothing to add.
 */
export const applyEntryPointAnswers = (
	schema: IRegEdMetadataForm | null | undefined,
	answers: Record<string, unknown>,
	campaignType: CampaignType | undefined,
	socialProviders?: readonly Api.SocialMediaType[]
): Record<string, unknown> | undefined => {
	let next = answers;
	const material = resolveContentTypeAnswer(schema, campaignType);
	if (material && isAnswerEmpty(next[material.fieldId])) {
		next = { ...next, [material.fieldId]: material.value };
	}
	const platforms = resolvePlatformAnswer(schema, next, socialProviders);
	if (platforms && isAnswerEmpty(next[platforms.fieldId])) {
		next = { ...next, [platforms.fieldId]: platforms.values };
	}
	return next === answers ? undefined : next;
};
